import { EventMessage } from '../../core/message';
import { Session } from '../../core/session';
import { isVideoRoomPluginMessage, VideoRoomPluginMessage } from './pluginData';
import { PluginHandler } from '../../core/pluginHandler';
import { UnexpectedResponse } from '../../core/transport';
import {
    CreateRequest,
    CreatedResponse,
    EditRequest,
    EditedResponse,
    DestroyRequest,
    DestroyedResponse,
    ExistsRequest,
    ExistsSuccessResponse,
    AllowedRequest,
    AllowedSuccessResponse,
    KickRequest,
    KickSuccessResponse,
    ListRequest,
    ListSuccessResponse,
    ListParticipantsRequest,
    ParticipantsResponse,
    PublisherJoinRequest,
    JoinedResponse,
    ConfiguredResponse,
    UnpublishRequest,
    UnpublishedResponse,
    PublisherConfigureRequest,
    LeaveRequest,
    LeavingResponse,
    LeftResponse,
    SubscriberJoinRequest,
    AttachedResponse,
    StartRequest,
    StartedResponse,
    PauseRequest,
    PausedResponse,
    SubscriberConfigureRequest,
    SwitchRequest,
    SwitchedResponse,
    Request,
    Event,
    AudioLevelEvent,
    DestroyedEvent,
    JoiningEvent,
    UnpublishedEvent,
    LeavingEvent,
    ActivePublishersEvent,
    PublishRequest,
    JoinAndPublisherConfigureRequest,
    PublisherSlowLinkEvent,
    SubscriberSlowLinkEvent,
    EnableRecordingRequest,
    EnableRecordingResponse
} from './requests';
import {
    RoomId,
    ParticipantId,
    VideoCodec,
    KNOWN_VIDEO_CODECS,
} from './common';
import { VideoRoomPluginUnexpectedResponse, checkError } from './errors';

interface VideoRoomEventListener {
    onjoining: (data: JoiningEvent) => void;
    onunpublished: (data: UnpublishedEvent) => void;
    onleaving: (data: LeavingEvent) => void;
    onpublish: (data: ActivePublishersEvent) => void;
    ondestroyed: (data: DestroyedEvent) => void;
    onaudiolevel: (data: AudioLevelEvent) => void;
    onvideoroomslowlink: (
        data: PublisherSlowLinkEvent | SubscriberSlowLinkEvent
    ) => void;
}

export type RoomOptions = {
    readonly videoCodecs: VideoCodec[];
}

export class VideoRoomPluginHandler extends PluginHandler {
    private listener: VideoRoomEventListener;

    static async create(session: Session, opaqueId: string) {
        const handleId = await session.attach(opaqueId, 'janus.plugin.videoroom');

        return new VideoRoomPluginHandler(session, handleId);
    }

    constructor(
        session: Session,
        handleId: number,
        listener: Partial<VideoRoomEventListener> = {}
    ) {
        super(session, handleId);

        this.listener = {
            onjoining: () => 0,
            onunpublished: () => 0,
            onleaving: () => 0,
            onpublish: () => 0,
            ondestroyed: () => 0,
            onaudiolevel: () => 0,
            onvideoroomslowlink: () => 0,
            ...listener,
        };
    }

    private _sendRequest(
        request: CreateRequest
    ): Promise<{ data: CreatedResponse }>
    private _sendRequest(request: EditRequest): Promise<{ data: EditedResponse }>
    private _sendRequest(
        request: DestroyRequest
    ): Promise<{ data: DestroyedResponse }>
    private _sendRequest(
        request: ExistsRequest
    ): Promise<{ data: ExistsSuccessResponse }>
    private _sendRequest(
        request: AllowedRequest
    ): Promise<{ data: AllowedSuccessResponse }>
    private _sendRequest(
        request: KickRequest
    ): Promise<{ data: KickSuccessResponse }>
    private _sendRequest(
        request: ListRequest
    ): Promise<{ data: ListSuccessResponse }>
    private _sendRequest(
        request: ListParticipantsRequest
    ): Promise<{ data: ParticipantsResponse }>
    private _sendRequest(
        request: PublisherJoinRequest
    ): Promise<{ data: JoinedResponse }>
    private _sendRequest(
        request: PublishRequest,
        jsep: RTCSessionDescriptionInit
    ): Promise<{ data: ConfiguredResponse; jsep: RTCSessionDescriptionInit }>
    private _sendRequest(
        request: JoinAndPublisherConfigureRequest
    ): Promise<{ data: JoinedResponse; jsep: RTCSessionDescriptionInit }>
    private _sendRequest(
        request: UnpublishRequest
    ): Promise<{ data: UnpublishedResponse }>
    private _sendRequest(
        request: PublisherConfigureRequest,
        jsep?: RTCSessionDescriptionInit
    ): Promise<{ data: ConfiguredResponse; jsep?: RTCSessionDescriptionInit }>
    // private _sendRequest(request: RtpForwardRequest): Promise<{data:RtpForwardResponse>
    // private _sendRequest(request: StopRtpForwardRequest): Promise<{data:StopRtpForwardResponse>
    // private _sendRequest(request: ListForwardersRequest): Promise<{data:ForwardersResponse>
    private _sendRequest(
        request: LeaveRequest
    ): Promise<{ data: LeavingResponse | LeftResponse }>
    private _sendRequest(
        request: SubscriberJoinRequest
    ): Promise<{ data: AttachedResponse; jsep: RTCSessionDescriptionInit }>
    private _sendRequest(
        request: StartRequest,
        jsep: RTCSessionDescriptionInit
    ): Promise<{ data: StartedResponse }>
    private _sendRequest(
        request: PauseRequest
    ): Promise<{ data: PausedResponse }>
    private _sendRequest(
        request: SubscriberConfigureRequest
    ): Promise<{ data: ConfiguredResponse }>
    private _sendRequest(
        request: SwitchRequest
    ): Promise<{ data: SwitchedResponse }>
    private _sendRequest(
        request: EnableRecordingRequest
    ): Promise<{ data: EnableRecordingResponse }>
    private async _sendRequest(
        request: Request,
        jsep?: RTCSessionDescriptionInit
    ): Promise<{
        data: VideoRoomPluginMessage['plugindata']['data'];
        jsep?: RTCSessionDescriptionInit;
    }> {
        const msg = await this.sendMessage({ body: request, jsep });

        const error = checkError(msg, request);
        if (error) {
            throw error;
        }

        // FIXME: ensure data is of Response (not Event)

        return {
            data: msg.plugindata.data,
            jsep: 'jsep' in msg ? msg.jsep : undefined,
        };
    }

    async createRoom(room: RoomId, options: RoomOptions = { videoCodecs: KNOWN_VIDEO_CODECS }) {
        const { videoCodecs } = options;
        const { data } = await this._sendRequest({
            request: 'create',
            room,
            'is_private': true,
            videocodec: videoCodecs.join(','),
        });
        return data;
    }

    async joinPublisher(room: RoomId, display: string) {
        const { data } = await this._sendRequest({
            request: 'join',
            room,
            ptype: 'publisher',
            display,
        });

        // check
        const r = data as VideoRoomPluginMessage['plugindata']['data'];
        if ('videoroom' in r && r.videoroom === 'joined') {
            // OK
        } else {
            throw new VideoRoomPluginUnexpectedResponse(data);
        }

        return data;
    }

    async leave(): Promise<void> {
        await this._sendRequest({ request: 'leave' });
    }

    async publish(
        settings: { videocodec: string },
        jsep: RTCSessionDescriptionInit
    ) {
        const ret = await this._sendRequest(
            {
                request: 'publish',
                ...settings,
            },
            jsep
        );
        return ret;
    }

    async configure(
        settings: { audio: boolean; video: boolean },
        jsep: RTCSessionDescriptionInit
    ) {
        return await this._sendRequest(
            {
                request: 'configure',
                ...settings,
            },
            jsep
        );
    }

    async joinSubscriber(
        room: RoomId,
        feed: ParticipantId,
        privateId: string | undefined
    ) {
        const response = await this._sendRequest({
            request: 'join',
            room,
            ptype: 'subscriber',
            feed,
            'private_id': privateId,
        });

        return response;
    }

    async start(jsep: RTCSessionDescriptionInit) {
        return await this._sendRequest({ request: 'start' }, jsep);
    }

    async enableRecording(room: RoomId, enabled: boolean, secret?: string) {
        return await this._sendRequest({
            request: 'enable_recording',
            room,
            secret,
            record: enabled,
        });
    }

    addListener(listener: Partial<VideoRoomEventListener>) {
        this.listener = { ...this.listener, ...listener };
    }

    onevent(msg: EventMessage) {
        if (!isVideoRoomPluginMessage(msg)) {
            throw new UnexpectedResponse(msg);
        }

        const data = msg.plugindata.data as Event;

        switch (data.videoroom) {
            case 'event':
                if ('joining' in data) {
                    this.listener.onjoining(data);
                } else if ('unpublished' in data) {
                    this.listener.onunpublished(data);
                } else if ('leaving' in data) {
                    this.listener.onleaving(data);
                } else {
                    this.listener.onpublish(data);
                }
                return;
            case 'destroyed':
                this.listener.ondestroyed(data);
                return;
            case 'talking':
            case 'stopped-talking':
                this.listener.onaudiolevel(data);
                return;
            case 'slow_link':
                this.listener.onvideoroomslowlink(data);
                return;
        }
    }

    protected async sendMessage<T extends {}>(payload: T) {
        const msg = await super.sendMessage(payload);
        if (!isVideoRoomPluginMessage(msg)) {
            throw new UnexpectedResponse(msg);
        }
        return msg;
    }
}
